import React, { useState, useEffect } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import {
  BookOpen,
  Clock,
  Loader,
  AlertCircle,
  User,
  CheckCircle,
  XCircle,
  Search,
  Filter,
  FileText,
  ShieldCheck,
  ExternalLink,
  Eye,
  Building,
  Mail,
  Phone,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  lmsGetStudentCourseRequests,
  lmsApproveStudentCourseRequest,
  lmsRejectStudentCourseRequest,
} from "../api/lmsApi";
import "../lms.css";

const getAdminData = () => {
  try {
    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    const adminId = ud.admin_id || ud.school_id || ud.id || null;
    const schoolId = ud.school_id || ud.admin_id || ud.parent_id || null;
    return { adminId, schoolId, ...ud };
  } catch {
    return { adminId: null, schoolId: null };
  }
};

export default function StudentCourseRequests() {
  const { studentId: paramStudentId } = useParams();
  const outletContext = useOutletContext() || {};
  const { adminId, schoolId: localSchoolId } = getAdminData();

  const studentId = paramStudentId || "all";
  const schoolId = outletContext.schoolId || localSchoolId;

  const [courseRequests, setCourseRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionLoading, setActionLoading] = useState({});
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    fetchCourseRequests();
  }, [studentId, schoolId]);

  const fetchCourseRequests = async () => {
    setLoading(true);
    try {
      const res = await lmsGetStudentCourseRequests(studentId, schoolId);
      const data = res.data?.data || (Array.isArray(res.data) ? res.data : []);
      setCourseRequests(data);
    } catch (err) {
      console.error("Error fetching course requests:", err);
      setCourseRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reqId, courseName, studentName) => {
    setActionLoading((prev) => ({ ...prev, [reqId]: "approving" }));
    try {
      await lmsApproveStudentCourseRequest(reqId, { verified_by: adminId });
      toast.success(
        `Approved enrollment for ${studentName || "student"} in "${courseName}"!`
      );
      fetchCourseRequests();
    } catch (err) {
      console.error("Error approving course request:", err);
      toast.error(err.response?.data?.message || "Failed to approve course request.");
    } finally {
      setActionLoading((prev) => ({ ...prev, [reqId]: null }));
    }
  };

  const handleReject = async (reqId, courseName, studentName) => {
    if (
      !window.confirm(
        `Are you sure you want to reject ${studentName || "student"}'s request for "${courseName}"?`
      )
    ) {
      return;
    }

    setActionLoading((prev) => ({ ...prev, [reqId]: "rejecting" }));
    try {
      await lmsRejectStudentCourseRequest(reqId, {
        rejection_reason: "Rejected by school administration",
      });
      toast.info(`Rejected course request for "${courseName}".`);
      fetchCourseRequests();
    } catch (err) {
      console.error("Error rejecting course request:", err);
      toast.error(err.response?.data?.message || "Failed to reject course request.");
    } finally {
      setActionLoading((prev) => ({ ...prev, [reqId]: null }));
    }
  };

  const filteredRequests = courseRequests.filter((req) => {
    const cName = (req.course_name || "").toLowerCase();
    const sFirstName = (req.student_first_name || "").toLowerCase();
    const sLastName = (req.student_last_name || "").toLowerCase();
    const sName = `${sFirstName} ${sLastName}`;
    const admNo = (req.student_admission_number || "").toLowerCase();
    const txn = (req.transaction_id || "").toLowerCase();
    const query = searchTerm.toLowerCase();

    return (
      cName.includes(query) ||
      sName.includes(query) ||
      admNo.includes(query) ||
      txn.includes(query)
    );
  });

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "60px 0" }}>
        <Loader size={32} className="spinner" style={{ margin: "0 auto 12px" }} />
        <p style={{ color: "#64748b", fontSize: "15px" }}>Loading pending course requests...</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      {/* Top Controls & Search Bar */}
      <div
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          padding: "16px 20px",
          border: "1px solid #e2e8f0",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: "1 1 280px" }}>
          <Search size={18} color="#94a3b8" />
          <input
            type="text"
            placeholder="Search by student name, course title, UTR, admission no..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              border: "none",
              outline: "none",
              fontSize: "14px",
              width: "100%",
              color: "#1e293b",
            }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            type="button"
            onClick={fetchCourseRequests}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 14px",
              borderRadius: "8px",
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              color: "#475569",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <span style={{ fontSize: "13px", color: "#64748b", fontWeight: "600" }}>
            {filteredRequests.length} {filteredRequests.length === 1 ? "Request" : "Requests"} Pending Review
          </span>
        </div>
      </div>

      {/* Pending Course Requests List */}
      {filteredRequests.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "50px 20px",
            background: "#ffffff",
            borderRadius: "12px",
            border: "1px dashed #cbd5e1",
          }}
        >
          <Clock size={44} color="#94a3b8" style={{ margin: "0 auto 12px" }} />
          <h4 style={{ fontSize: "16px", fontWeight: "600", color: "#334155" }}>
            No Pending Course Requests
          </h4>
          <p style={{ fontSize: "13px", color: "#94a3b8", marginTop: "4px" }}>
            {searchTerm
              ? "No pending requests match your search."
              : "All student course enrollment requests have been reviewed and processed."}
          </p>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
            gap: "20px",
          }}
        >
          {filteredRequests.map((req) => {
            const reqId = req.id || req.enrollment_id;
            const origPrice = parseFloat(req.course_price) || 0;
            const discPrice = parseFloat(req.course_discount_price) || origPrice;
            const targetFee = discPrice || origPrice;
            const paidAmt = parseFloat(req.requested_amount || req.total_paid_amount || 0);
            const studentFullName =
              req.student_first_name || req.student_last_name
                ? `${req.student_first_name || ""} ${req.student_last_name || ""}`.trim()
                : `Student #${req.student_id}`;

            const isProcessing = actionLoading[reqId];

            return (
              <div
                key={reqId}
                style={{
                  background: "#ffffff",
                  borderRadius: "12px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header Banner */}
                <div
                  style={{
                    padding: "14px 18px",
                    background: "#fef9c3",
                    borderBottom: "1px solid #fef08a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span
                    style={{
                      background: "#fef3c7",
                      color: "#b45309",
                      padding: "3px 10px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "700",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <Clock size={13} /> Pending Review & Approval
                  </span>

                  <span style={{ fontSize: "11px", color: "#854d0e", fontWeight: "500" }}>
                    Submitted: {req.enrolled_at || req.created_at ? new Date(req.enrolled_at || req.created_at).toLocaleDateString("en-IN") : "Recently"}
                  </span>
                </div>

                {/* Card Body */}
                <div style={{ padding: "18px", flex: 1, display: "flex", flexDirection: "column" }}>
                  {/* Student Info Box */}
                  <div
                    style={{
                      background: "#f8fafc",
                      borderRadius: "8px",
                      padding: "12px 14px",
                      marginBottom: "14px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                      <User size={15} color="#1c498d" />
                      <strong style={{ fontSize: "14px", color: "#0f172a" }}>
                        {studentFullName}
                      </strong>
                      {req.student_admission_number && (
                        <span
                          style={{
                            background: "#e2e8f0",
                            color: "#334155",
                            padding: "1px 6px",
                            borderRadius: "4px",
                            fontSize: "11px",
                            fontWeight: "600",
                          }}
                        >
                          Adm: {req.student_admission_number}
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                      {req.student_email && (
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <Mail size={12} /> {req.student_email}
                        </span>
                      )}
                      {req.student_mobile && (
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <Phone size={12} /> {req.student_mobile}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Course Info */}
                  <div style={{ display: "flex", gap: "12px", marginBottom: "14px" }}>
                    <div
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "8px",
                        background: "#1c498d",
                        color: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "16px",
                        fontWeight: "700",
                        flexShrink: 0,
                      }}
                    >
                      {(req.course_name || "C").charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h4 style={{ fontSize: "15px", fontWeight: "600", color: "#0f172a", margin: 0, lineHeight: "1.3" }}>
                        {req.course_name || `Course #${req.course_id}`}
                      </h4>
                      {req.instructor_name && (
                        <span style={{ fontSize: "12px", color: "#64748b" }}>
                          Instructor: {req.instructor_name}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Payment Details Table Box */}
                  <div
                    style={{
                      background: "#f8fafc",
                      borderRadius: "8px",
                      padding: "12px 14px",
                      marginBottom: "16px",
                      fontSize: "13px",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                      <span style={{ color: "#64748b" }}>Course Fee:</span>
                      <strong style={{ color: "#334155" }}>₹{targetFee.toLocaleString("en-IN")}</strong>
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                      <span style={{ color: "#64748b" }}>Submitted Amount:</span>
                      <strong style={{ color: "#16a34a", fontSize: "14px" }}>₹{paidAmt.toLocaleString("en-IN")}</strong>
                    </div>

                    {req.transaction_id && (
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                        <span style={{ color: "#64748b" }}>Txn / UTR ID:</span>
                        <span style={{ fontFamily: "monospace", color: "#1c498d", fontWeight: "700" }}>
                          {req.transaction_id}
                        </span>
                      </div>
                    )}

                    {req.payment_screenshot && (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "4px" }}>
                        <span style={{ color: "#64748b" }}>Screenshot Proof:</span>
                        <button
                          type="button"
                          onClick={() => setPreviewImage(req.payment_screenshot)}
                          style={{
                            color: "#1c498d",
                            fontWeight: "600",
                            fontSize: "12px",
                            border: "none",
                            background: "none",
                            textDecoration: "underline",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          <Eye size={13} /> View Receipt Proof
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Admin Action Buttons */}
                  <div style={{ marginTop: "auto", display: "flex", gap: "10px", paddingTop: "12px", borderTop: "1px solid #f1f5f9" }}>
                    <button
                      type="button"
                      disabled={isProcessing}
                      onClick={() => handleReject(reqId, req.course_name, studentFullName)}
                      style={{
                        flex: 1,
                        padding: "9px 12px",
                        borderRadius: "8px",
                        border: "1px solid #fca5a5",
                        background: "#fff1f2",
                        color: "#991b1b",
                        fontWeight: "600",
                        fontSize: "13px",
                        cursor: isProcessing ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      <XCircle size={15} /> Reject
                    </button>

                    <button
                      type="button"
                      disabled={isProcessing}
                      onClick={() => handleApprove(reqId, req.course_name, studentFullName)}
                      style={{
                        flex: 1.5,
                        padding: "9px 12px",
                        borderRadius: "8px",
                        border: "none",
                        background: "#16a34a",
                        color: "#ffffff",
                        fontWeight: "600",
                        fontSize: "13px",
                        cursor: isProcessing ? "not-allowed" : "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      {isProcessing === "approving" ? (
                        <>
                          <Loader size={15} className="spinner" /> Approving...
                        </>
                      ) : (
                        <>
                          <CheckCircle size={15} /> Approve & Activate
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Screenshot Preview Modal */}
      {previewImage && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1100,
            background: "rgba(15, 23, 42, 0.75)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
          onClick={() => setPreviewImage(null)}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              maxWidth: "540px",
              width: "100%",
              overflow: "hidden",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                padding: "16px 20px",
                background: "#1c498d",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <h3 style={{ fontSize: "16px", fontWeight: "700", margin: 0 }}>
                Payment Screenshot Proof
              </h3>
              <button
                type="button"
                onClick={() => setPreviewImage(null)}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  borderRadius: "50%",
                  width: "30px",
                  height: "30px",
                  color: "#ffffff",
                  cursor: "pointer",
                }}
              >
                ✕
              </button>
            </div>
            <div style={{ padding: "20px", textAlign: "center", background: "#f8fafc" }}>
              <img
                src={previewImage}
                alt="Payment Receipt Screenshot"
                style={{
                  maxWidth: "100%",
                  maxHeight: "450px",
                  borderRadius: "8px",
                  objectFit: "contain",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                }}
              />
              <div style={{ marginTop: "14px" }}>
                <a
                  href={previewImage}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "6px",
                    color: "#1c498d",
                    fontWeight: "600",
                    fontSize: "13px",
                    textDecoration: "underline",
                  }}
                >
                  <ExternalLink size={14} /> Open Full Resolution Image
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
